import React, { useCallback, useLayoutEffect, useEffect, useMemo, useState, } from 'react';
import { StyleSheet, View } from 'react-native';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView } from 'design-system/AppView';
import SectionHeader from 'design-system/SectionHeader';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useNavigateToAnalytics from 'features/analytics/hooks/useSavePeriodAndNavigateToAnalytics';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Animated from 'react-native-reanimated';
import { Frequencies } from 'reducers/types';
import { getIncomes } from 'actions';
import Modal from 'utils/packages/Modal';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { goBackWithKey } from 'utils/navigationv6';
import FlatListItemCard from 'design-system/FlatListItemCard';
import RadioButton from 'design-system/RadioButton';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Config from 'utils/packages/configs';
import i18n from 'utils/i18n';
import { selectIsGBUser } from 'reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useSaveBudget } from '../hooks/useSaveBudget';
import { isWeb } from '../../../constants';
import { selectIncomes } from '../selectors';
import { getMonthToPeriodRatio, getPeriodFrequency, incomeToPeriod, toBudgetApiPayload, } from '../utils';
import { useBudgetRollover } from '../hooks/useBudgetRollover';
import { getBudgets } from '../actions';
const strings = {
    title: 'When should your period begin?',
    description: 'Your Budgeting and Analytics will align with this period',
    modalTitle: 'Period',
    modalDescription: 'The period is the cycle you will use to sync your budgets and analytics. For example, if you get paid on the last working day of each month you can set your period to this so you can track your spending throughout your pay cycle.',
};
const debugOptions = {
    label: 'Short 15 min period',
    value: Frequencies.Minutely,
};
const empty = {};
const SelectPeriodScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler, route }) => {
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BUDGETING}/>),
        });
    }, []);
    useEffect(() => {
        dispatch(getIncomes());
    }, []);
    const { flow, monthlyBudget, budgetState, saveAndGoToKeyBack, hasPrefilledBudgets, onSavePeriod, createOwnBudget, } = route.params;
    const income = useAppSelector(selectIncomes);
    const currentPeriod = route.params.period || incomeToPeriod(income);
    const period = route.params.period || currentPeriod || empty;
    const [isFetching, save] = useSaveBudget();
    const { isRollover } = useBudgetRollover();
    const navigateToAnalytics = useNavigateToAnalytics();
    const { isUnlockedForSpace } = useShouldShowUpgrade({
        benefitId: 'budgeting',
    });
    const isGBUser = useAppSelector(selectIsGBUser);
    const options = useMemo(() => {
        const unsortedOptions = [
            ...(Config?.IS_PROD === 'false' ? [debugOptions] : []), // debug
            {
                label: 'First day of the month',
                value: Frequencies.Monthly,
                usOrder: isUnlockedForSpace ? 4 : 0,
            },
            {
                label: 'Last working day of the month',
                value: Frequencies.LastWorkingDay,
                usOrder: 4,
            },
            {
                label: 'Penultimate working day of the month',
                value: Frequencies.PenultimateWorkingDay,
                usOrder: 8,
            },
            {
                label: 'Nth last working day of the month',
                value: Frequencies.NthLastWorkingDay,
                description: 'e.g. 3rd last working day of the month',
                title: 'Choose a working day before month-end',
                usOrder: 9,
            },
            {
                label: 'Fixed monthly date',
                title: 'Select the date',
                value: Frequencies.FixedDate,
                description: 'e.g. on the 15th of the month',
                description2: 'This should include both your day to day spending and recurring payments',
                usOrder: 5,
            },
            {
                label: 'Variable monthly date',
                title: 'Select the date',
                value: Frequencies.EveryMonth,
                description: 'e.g. on the 4th Friday of the month',
                description2: 'This should include both your day to day spending and recurring payments',
                usOrder: 6,
            },
            {
                label: i18n.t('TwiceAMonth'),
                title: 'Select 2 dates',
                value: Frequencies.TwiceAMonth,
                description: i18n.t('TwiceAMonthDesc'),
                description2: 'This should include both your day to day spending and recurring payments',
                usOrder: 3,
            },
            {
                label: 'Every 4 weeks',
                title: 'Select date',
                value: Frequencies.EveryFourWeeks,
                description2: 'Select the day of the week when you want your period to reset.',
                usOrder: 7,
            },
            {
                label: i18n.t('Every2weeks'),
                title: 'Select date',
                value: Frequencies.EveryTwoWeeks,
                description2: 'Select the day of the week when you want your period to reset.',
                usOrder: 1,
            },
            {
                label: 'Every week',
                title: 'Select date',
                value: Frequencies.Weekly,
                description2: 'Select the day of the week when you want your period to reset.',
                usOrder: 2,
            },
        ];
        if (!isGBUser) {
            unsortedOptions.sort((a, b) => (a.usOrder ?? 0) - (b.usOrder ?? 0));
        }
        if (!isUnlockedForSpace) {
            return unsortedOptions;
        }
        return unsortedOptions.sort((a, b) => {
            if (a.value === currentPeriod?.frequency)
                return -1;
            if (b.value === currentPeriod?.frequency)
                return 1;
            return 0;
        });
    }, [currentPeriod?.frequency, isGBUser, isUnlockedForSpace]);
    const [selected, setSelected] = useState(isUnlockedForSpace ? currentPeriod.frequency : options[0].value);
    const onPeriodSave = useCallback((cb, newPeriod) => {
        const currentMonthToPeriodRatio = getMonthToPeriodRatio(toBudgetApiPayload(currentPeriod));
        const newMonthToPeriodRatio = getMonthToPeriodRatio(toBudgetApiPayload(newPeriod));
        if (currentMonthToPeriodRatio === newMonthToPeriodRatio) {
            cb();
            return;
        }
        Modal.showConfirmation({
            title: 'Change period',
            subtitle: 'You are about to change your period. Would you also like to edit your budget?',
            confirmButtonTitle: 'No, just change period',
            cancelButtonTitle: 'Yes, update budget',
            onConfirm: cb,
            onPressCancel: () => navigation.navigateDeprecated('SetUpBudget', { newPeriod }),
            onBackdropPress: cb,
        });
    }, [currentPeriod]);
    const showAutoBudgetsAgain = useMemo(() => {
        if (!currentPeriod.frequency || !selected) {
            return false;
        }
        return (getPeriodFrequency(currentPeriod.frequency) !==
            getPeriodFrequency(selected));
    }, [currentPeriod.frequency, selected]);
    const onSave = useCallback(async () => {
        if (isFetching || !selected)
            return;
        const newPeriod = { ...period };
        if (selected !== currentPeriod.frequency) {
            newPeriod.frequency = selected;
        }
        const saveNewPeriod = async () => {
            await save(undefined, isRollover, [], newPeriod);
            dispatch(getIncomes());
            dispatch(getBudgets());
        };
        if (saveAndGoToKeyBack && route.params.keyBack) {
            await saveNewPeriod();
            if (hasPrefilledBudgets && showAutoBudgetsAgain) {
                navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                    flow: 'Set',
                    period: newPeriod,
                    resetKey: Date.now(),
                });
                onSavePeriod?.(newPeriod);
            }
            else {
                onPeriodSave(() => {
                    goBackWithKey(navigation, route.params.keyBack, false);
                    onSavePeriod?.(newPeriod);
                }, newPeriod);
            }
        }
        else if (flow === 'SetAnalytics') {
            saveNewPeriod();
            onPeriodSave(() => navigateToAnalytics(newPeriod), newPeriod);
        }
        else if (flow === 'Set') {
            if (!budgetState) {
                if (createOwnBudget) {
                    await saveNewPeriod();
                    navigation.navigateDeprecated('EditBudget', {
                        flow,
                        period: newPeriod,
                    });
                }
                else {
                    saveNewPeriod();
                    navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                        flow,
                        period: newPeriod,
                    });
                }
            }
            else {
                navigation.navigateDeprecated('Budgets', {
                    flow,
                    monthlyBudget,
                    budgetState,
                    period: newPeriod,
                });
            }
        }
        else if (hasPrefilledBudgets && showAutoBudgetsAgain) {
            saveNewPeriod();
            navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                flow: 'Set',
                period: newPeriod,
                resetKey: Date.now(),
            });
        }
        else {
            saveNewPeriod();
            onPeriodSave(() => navigation.navigateDeprecated('EditBudget', {
                flow,
                budgetState,
                period: newPeriod,
            }), newPeriod);
        }
    }, [
        budgetState,
        createOwnBudget,
        currentPeriod.frequency,
        flow,
        hasPrefilledBudgets,
        isFetching,
        isRollover,
        monthlyBudget,
        onPeriodSave,
        onSavePeriod,
        period,
        route.params.keyBack,
        save,
        saveAndGoToKeyBack,
        selected,
        showAutoBudgetsAgain,
    ]);
    const noFurtherSteps = useMemo(() => selected === Frequencies.PenultimateWorkingDay ||
        selected === Frequencies.Monthly ||
        selected === Frequencies.Minutely ||
        selected === Frequencies.LastWorkingDay, [selected]);
    const onSaveWithPaywall = useRequiredBenefitCallbackWithSpaces(useCallback(() => onSave(), [onSave]), 'budgeting');
    const onPressCustom = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        const option = options.find((o) => o.value === selected);
        if (isFetching || !option)
            return;
        const onSaveAnalytics = async (period) => {
            await save(undefined, isRollover, [], period);
            dispatch(getIncomes());
            dispatch(getBudgets());
            if (saveAndGoToKeyBack && route.params.keyBack) {
                onPeriodSave(() => {
                    goBackWithKey(navigation, route.params.keyBack, false);
                    onSavePeriod?.(period);
                }, period);
            }
            else {
                onPeriodSave(() => navigateToAnalytics(period), period);
            }
        };
        navigation.navigateDeprecated('EditBudgetFrequency', {
            flow,
            monthlyBudget,
            budgetState,
            currentPeriod,
            period,
            option,
            hasPrefilledBudgets,
            createOwnBudget,
            onSavePeriod: route.params.flow === 'SetAnalytics' ? onSaveAnalytics : undefined,
        });
    }, [
        budgetState,
        createOwnBudget,
        currentPeriod,
        flow,
        hasPrefilledBudgets,
        isFetching,
        isRollover,
        monthlyBudget,
        onPeriodSave,
        onSavePeriod,
        options,
        period,
        route.params.flow,
        route.params.keyBack,
        save,
        saveAndGoToKeyBack,
        selected,
    ]), 'budgeting');
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const onContinue = useCallback(() => {
        switch (selected) {
            case Frequencies.Monthly:
            case Frequencies.Minutely:
                onSave();
                break;
            case Frequencies.LastWorkingDay:
            case Frequencies.PenultimateWorkingDay:
                onSaveWithPaywall();
                break;
            default:
                onPressCustom();
        }
    }, [onPressCustom, onSave, onSaveWithPaywall, selected]);
    const showModal = useCallback(() => {
        Modal.show(<>
          <Spacer h={8}/>
          <Text TextThin-14 Gray centered={!isWeb}>
            {strings.modalDescription}
          </Text>
          <Spacer h={40}/>
          <Button brand title="Got it" onPress={() => {
                Modal.hide(undefined, { top: true });
            }}/>
        </>, { title: strings.modalTitle, top: true });
    }, []);
    return (<AppView withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom}>
          <ScreenInfoHeader title={strings.title} subTitle={<>
                <Text TextThin-16 Gray>
                  {strings.description}{' '}
                </Text>
                <Spacer.H8 />
                <Text Text-16 Primary onPress={showModal} suppressHighlighting>
                  Learn more
                </Text>
              </>}/>
          {!isUnlockedForSpace && (<>
              <SectionHeader horizontalMargins>Your plan</SectionHeader>
              <Spacer.H8 />
            </>)}
          <FlatListItemCard index={0} itemsLength={isUnlockedForSpace ? 2 : 1} onPress={() => setSelected(options[0].value)} style={styles.card}>
            <Item label={options[0].label} description={options[0].description} isSelected={selected === options[0].value}/>
          </FlatListItemCard>

          {!isUnlockedForSpace && (<>
              <Spacer.H8 />
              <SectionHeader verticalMargins horizontalMargins>
                Premium plans
              </SectionHeader>
            </>)}
          {options.slice(1).map((option, index) => (<FlatListItemCard key={option.value} index={isUnlockedForSpace ? index + 1 : index} itemsLength={isUnlockedForSpace ? options.length : options.length - 1} onPress={() => setSelected(option.value)} style={styles.card}>
              <Item label={option.label} description={option.description} isSelected={selected === option.value}/>
            </FlatListItemCard>))}
        </Animated.ScrollView>
        <Button brand floating title={noFurtherSteps ? 'Save' : 'Continue'} onPress={onContinue} isFetching={isFetching} disabled={!selected} containerStyle={{ opacity: selected ? 1 : 0 }}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    title: 'Select your period',
    hideTitleOnScroll: true,
}));
const Item = ({ label, description, isSelected }) => (<View style={staticStyles.row}>
    <View style={staticStyles.flex}>
      <Text Text-16 flex>
        {label}
      </Text>
      {!!description && <Text TextThin-14>{description}</Text>}
    </View>
    <Spacer w={10}/>
    <RadioButton type="single" isChecked={isSelected} testID="SelectPeriodScreenRadio"/>
  </View>);
const staticStyles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: rem(20),
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
});
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default SelectPeriodScreen;
